import { useCallback, useState } from 'react';
import { useExperiments, useTranslation } from '@wix/fe-essentials-standalone';
import StatusToastNotification from '@components/StatusToastNotification';
import { TemplateGalleryCard } from '@common/types';
import { useHeaderContext } from '@components/Contexts/HeaderContext';
import { useTemplatesBi } from '@components/Hooks/useTemplatesBi';
import { redirectToLoginWithFavorite } from '@utils/urlUtils';
import {
  ENABLE_TEMPLATE_FAVORITES_SPEC,
  TEMPLATES_GALLERY_ENTRY,
} from '@utils/constants';
import { FAVORITES_BI_CLICK_TYPES } from './useFavoritesBi';
import FavoriteButton from './FavoriteButton';
import { useTemplateFavorites } from './useTemplateFavorites';
import { useAutoFavoriteFromQueryParam } from './useAutoFavoriteFromQueryParam';

export function useTemplateFavoritesGallery(isMobileView: boolean) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { userDetails, loading: isHeaderLoading } = useHeaderContext();
  const { experiments } = useExperiments({ readOnly: true });
  const { logBiClick } = useTemplatesBi({ is_mobile: isMobileView });

  const shouldShowFavoriteButton = experiments.enabled(
    ENABLE_TEMPLATE_FAVORITES_SPEC,
  );
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [failedToggleAction, setFailedToggleAction] = useState<
    'add' | 'remove' | null
  >(null);
  const errorKeySuffix = isMobileView ? '.mobile' : '';

  const {
    favoriteTemplateIds,
    toggleFavoriteTemplate,
    pendingFavoriteListingId,
    isTogglePending,
  } = useTemplateFavorites({
    enabled: shouldShowFavoriteButton && Boolean(userDetails?.email),
    onToggleError: (shouldFavorite) =>
      setFailedToggleAction(shouldFavorite ? 'add' : 'remove'),
  });

  useAutoFavoriteFromQueryParam({
    enabled: shouldShowFavoriteButton && Boolean(userDetails?.email),
    toggle: (id, opts) =>
      toggleFavoriteTemplate({ listingId: id, shouldFavorite: true }, opts),
    onSuccess: () => setShowSuccessToast(true),
  });

  const getFavoriteButton = useCallback(
    (template: TemplateGalleryCard) => {
      if (!shouldShowFavoriteButton || !template.id) return undefined;
      return (
        <FavoriteButton
          listingId={template.id}
          isFavorite={favoriteTemplateIds.has(template.id)}
          isLoading={
            isHeaderLoading ||
            (isTogglePending && pendingFavoriteListingId === template.id)
          }
          addLabel={t('templatesFavorites.gallery.tooltip.add')}
          removeLabel={t('templatesFavorites.gallery.tooltip.remove')}
          onToggle={(listingId, shouldFavorite) => {
            if (!userDetails?.email) {
              redirectToLoginWithFavorite(listingId, language);
              return;
            }
            toggleFavoriteTemplate({ listingId, shouldFavorite });
            logBiClick({
              click_type: shouldFavorite
                ? FAVORITES_BI_CLICK_TYPES.addToFavorites
                : FAVORITES_BI_CLICK_TYPES.removeFromFavorites,
              selected_template_id: listingId,
              entry: TEMPLATES_GALLERY_ENTRY,
            });
          }}
        />
      );
    },
    [
      favoriteTemplateIds,
      isHeaderLoading,
      isTogglePending,
      language,
      logBiClick,
      pendingFavoriteListingId,
      shouldShowFavoriteButton,
      t,
      toggleFavoriteTemplate,
      userDetails?.email,
    ],
  );

  const toasts = (
    <>
      {showSuccessToast && (
        <StatusToastNotification
          key={String(showSuccessToast)}
          status="success"
          message={t(
            'templatesFavorites.gallery.statusToast.success.description',
          )}
          onDismiss={() => setShowSuccessToast(false)}
          dismissible
          isMobileView={isMobileView}
        />
      )}
      {failedToggleAction !== null && (
        <StatusToastNotification
          key={failedToggleAction}
          status="error"
          message={t(
            `templatesFavorites.statusToast.${failedToggleAction}Failure${errorKeySuffix}`,
          )}
          onDismiss={() => setFailedToggleAction(null)}
          dismissible
          isMobileView={isMobileView}
        />
      )}
    </>
  );

  return { getFavoriteButton, toasts };
}
